import React, { useState, useRef, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Brush, ReferenceLine } from 'recharts';
import { Upload, Download, X, Check, Loader2, File as FileIcon, LogOut, Eye, EyeOff, Clock } from 'lucide-react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip as LTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import * as XLSX from 'xlsx';

const RoadVisionLogo = ({ height = 32 }) => (
  <svg height={height} viewBox="0 0 370 60" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <polygon points="26,4 4,52 48,52" fill="currentColor"/>
    <line x1="26" y1="14" x2="26" y2="52" stroke="white" strokeWidth="3" strokeDasharray="8,5"/>
    <text x="60" y="50" fontFamily="'Helvetica Neue', Arial, sans-serif" fontSize="38" fontWeight="700" letterSpacing="1.5" fill="currentColor">ROADVISION</text>
  </svg>
);

const C = {
  canvas: '#EAEBE7', ink: '#15211F', sub: '#5A615C', card: '#FBFBF9',
  line: '#D4D7D1', asphalt: '#1B201E', petrol: '#1C6F69', petrolDark: '#155551',
  petrolTint: '#DCEAE8', laneDash: '#EDE9DC', error: '#991B1B', errorBg: '#FEF2F2', errorBorder: '#FECACA',
};

const STAGES = [
  'Cleaning and segmenting road surface',
  'Classifying wheel tracks',
  'Computing MPD',
];

function humanSize(b) {
  if (b == null) return '';
  const gb = b / 1e9;
  return gb >= 1 ? gb.toFixed(2) + ' GB' : (b / 1e6).toFixed(0) + ' MB';
}

function Tip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const side = payload[0].dataKey;
  return (
    <div style={{
      background: C.ink, color: '#fff', borderRadius: 8, padding: '7px 11px',
      fontFamily: 'ui-monospace, monospace', fontSize: 12, lineHeight: 1.5,
    }}>
      <div style={{ opacity: .6 }}>{d.m} m</div>
      <div style={{ fontSize: 14 }}>{d[side]?.toFixed(3)} mm</div>
      {d.lat != null && (
        <div style={{ opacity: .5, marginTop: 2, fontSize: 10.5 }}>{d.lat.toFixed(5)}, {d.lon.toFixed(5)}</div>
      )}
    </div>
  );
}

function MpdChart({ title, subtitle, data, dataKey, mean, color, showBrush, brushLen }) {
  const yMin = 0;
  const yMax = Math.ceil(Math.max(...data.map(d => d[dataKey])) * 10 + 1) / 10;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: '16px 14px 6px', flex: 1, minWidth: 0 }}>
      <div style={{ padding: '0 4px 2px' }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{title}</div>
        <div className="rv-mono" style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>{subtitle}</div>
      </div>
      <div style={{ height: showBrush ? 270 : 230, marginTop: 6 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 8, left: -18, bottom: showBrush ? 4 : 0 }}>
            <XAxis dataKey="m" tickLine={false} axisLine={{ stroke: C.line }}
              tick={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, fill: C.sub }}
              tickFormatter={(v) => v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v}
              minTickGap={30} />
            <YAxis tickLine={false} axisLine={false} domain={[yMin, yMax]}
              tick={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, fill: C.sub }} />
            <Tooltip content={<Tip />} />
            <ReferenceLine y={mean} stroke={C.ink} strokeDasharray="4 4" strokeOpacity={0.4}
              label={{ value: `mean ${mean.toFixed(2)}`, position: 'right', fontSize: 10, fill: C.sub, fontFamily: 'ui-monospace, monospace' }} />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.3} dot={false} isAnimationActive={false} />
            {showBrush && (
              <Brush dataKey="m" height={26} stroke={C.line} fill={C.canvas}
                startIndex={0} endIndex={Math.min(brushLen, data.length - 1)}
                tickFormatter={(v) => v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v + 'm'} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Readout({ label, value, unit }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: '14px 16px', flex: 1, minWidth: 0 }}>
      <div className="rv-mono" style={{ fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.sub }}>{label}</div>
      <div style={{ marginTop: 7, display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span className="rv-mono" style={{ fontSize: 26, color: C.ink, fontWeight: 500 }}>{value}</span>
        <span className="rv-mono" style={{ fontSize: 12, color: C.sub }}>{unit}</span>
      </div>
    </div>
  );
}

function SectionMap({ data }) {
  const [selected, setSelected] = useState(null);
  const positions = useMemo(() => data.filter(d => d.lat != null).map(d => [d.lat, d.lon]), [data]);
  if (positions.length < 2) return null;
  const midIdx = Math.floor(positions.length / 2);
  const center = positions[midIdx];
  const handleClick = (e) => {
    const { lat, lng } = e.latlng;
    let bestIdx = 0, bestDist = Infinity;
    for (let i = 0; i < data.length; i++) {
      if (data[i].lat == null) continue;
      const dLat = data[i].lat - lat;
      const dLon = data[i].lon - lng;
      const dist = dLat * dLat + dLon * dLon;
      if (dist < bestDist) { bestDist = dist; bestIdx = i; }
    }
    setSelected(data[bestIdx]);
  };
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden', marginTop: 20 }}>
      <div style={{ padding: '16px 18px 8px' }}>
        <div style={{ fontSize: 15, fontWeight: 500 }}>Section map</div>
        <div className="rv-mono" style={{ fontSize: 11.5, color: C.sub, marginTop: 3 }}>Click on the road to see MPD at that point</div>
      </div>
      <div style={{ height: 340, position: 'relative' }}>
        <MapContainer center={center} zoom={14} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true} attributionControl={true}>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Polyline positions={positions} color={C.petrol} weight={4} opacity={0.8} eventHandlers={{ click: handleClick }} />
          {selected && (
            <CircleMarker center={[selected.lat, selected.lon]} radius={6} fillColor={C.petrol} fillOpacity={1} stroke={true} color="#fff" weight={2}>
              <LTooltip direction="top" offset={[0, -8]} permanent>
                <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, lineHeight: 1.6 }}>
                  <div><strong>{selected.m} m</strong></div>
                  <div>Left: {selected.left?.toFixed(3)} mm</div>
                  <div>Right: {selected.right?.toFixed(3)} mm</div>
                  <div style={{ opacity: 0.6, marginTop: 2 }}>{selected.lat.toFixed(5)}, {selected.lon.toFixed(5)}</div>
                </div>
              </LTooltip>
            </CircleMarker>
          )}
        </MapContainer>
      </div>
    </div>
  );
}

export default function App() {
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState('');
  const [userRole, setUserRole] = useState('user');
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const [phase, setPhase] = useState('idle');
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [shpFile, setShpFile] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  const [logModal, setLogModal] = useState({ open: false, content: '', loading: false });
  
  const inputRef = useRef(null);
  const shpRef = useRef(null);
  const upRef = useRef(null);
  const procRef = useRef(null);

  useEffect(() => () => { clearInterval(upRef.current); clearInterval(procRef.current); }, []);

  const handleLogin = async () => {
    setLoginError(null);
    setLoginLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUser, password: loginPass }),
      });
      const data = await res.json();
      if (!res.ok) { setLoginError(data.detail || 'Login failed'); return; }
      setToken(data.token);
      setUsername(data.username);
      setUserRole(data.role || 'user');
      setLoginPass(''); setShowPass(false);
      try {
        const hRes = await fetch('/api/jobs', { headers: { 'Authorization': `Bearer ${data.token}` } });
        if (hRes.ok) setHistory(await hRes.json());
      } catch (e) {}
    } catch {
      setLoginError('Cannot reach the server');
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    setToken(null); setUsername(''); setUserRole('user'); setLoginUser(''); setLoginPass('');
    reset();
  };

  const isValid = (name) => /\.(las|laz)$/i.test(name);

  const select = (name, bytes, fileObj = null) => {
    setError(null);
    if (!isValid(name)) {
      setError(`"${name}" is not a valid point cloud. Please upload a .las or .laz file.`);
      return;
    }
    setFile({ name, size: humanSize(bytes), obj: fileObj });
    setPhase('selected');
  };
  const onInput = (e) => { const f = e.target.files?.[0]; if (f) select(f.name, f.size, f); };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) select(f.name, f.size, f); };

  const onShpInput = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    const REQUIRED = ['.shp', '.dbf', '.prj'];
    const exts = picked.map(f => f.name.toLowerCase().match(/\.[^.]+$/)?.[0] || '');
    const missing = REQUIRED.filter(r => !exts.includes(r));
    const extra = exts.filter(e => !REQUIRED.includes(e));
    if (picked.length !== 3 || missing.length > 0 || extra.length > 0) {
      setError(`Select exactly 3 files: .shp, .dbf and .prj.${missing.length ? ' Missing: ' + missing.join(', ') : ''}${extra.length ? ' Unexpected: ' + extra.join(', ') : ''}`);
      if (shpRef.current) shpRef.current.value = '';
      return;
    }
    setError(null);
    setShpFile({ names: picked.map(f => f.name), objs: picked });
  };

  const authHeaders = () => ({ 'Authorization': `Bearer ${token}` });

  const fetchHistory = async () => {
    try {
      const res = await fetch('/api/jobs', { headers: authHeaders() });
      if (res.ok) setHistory(await res.json());
    } catch (e) { console.error('Failed to fetch history', e); }
  };

  const loadHistoryJob = async (jobId, filename) => {
    try {
      const res = await fetch(`/api/jobs/${jobId}/results`, { headers: authHeaders() });
      if (!res.ok) { setError('Could not load results for this job.'); return; }
      const data = await res.json();
      setResults(data);
      setFile({ name: filename, size: '' });
      setPhase('done');
      setShowHistory(false);
    } catch (e) { setError('Failed to load results.'); }
  };

  const fetchLog = async (jobId) => {
    setLogModal({ open: true, content: '', loading: true });
    try {
      const res = await fetch(`/api/jobs/${jobId}/log`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Falha ao carregar logs.');
      setLogModal({ open: true, content: data.log, loading: false });
    } catch (e) {
      setLogModal({ open: true, content: e.message, loading: false });
    }
  };

  const start = async () => {
    if (!file?.obj) { setError('Please select a point cloud file.'); setPhase('idle'); return; }
    if (!shpFile?.objs?.length) { setError('Please select the 3 shapefile files (.shp, .dbf, .prj).'); return; }
    setPhase('uploading'); setProgress(0);
    try {
      const form = new FormData();
      form.append('file', file.obj);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/upload');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
      };
      const uploadResult = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
          else reject(new Error(JSON.parse(xhr.responseText).detail || 'Upload failed'));
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(form);
      });

      let shpFileId = '';
      if (shpFile?.objs?.length === 3) {
        const shpForm = new FormData();
        shpFile.objs.forEach(f => shpForm.append('files', f));
        const shpRes = await fetch('/api/upload-shapefile', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: shpForm,
        });
        if (!shpRes.ok) {
          const shpErr = await shpRes.json();
          throw new Error(shpErr.detail || 'Shapefile upload failed');
        }
        const shpData = await shpRes.json();
        shpFileId = shpData.shapefile_id;
      }

      const jobRes = await fetch('/api/jobs', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_id: uploadResult.file_id,
          filename: uploadResult.filename,
          file_size: uploadResult.size_bytes,
          shapefile_file_id: shpFileId,
        }),
      });
      const job = await jobRes.json();
      if (!jobRes.ok) throw new Error(job.detail || 'Could not create job');
      setPhase('processing'); setStage(0);
      pollJob(job.job_id);
    } catch (err) {
      setError(err.message || 'Something went wrong');
      setPhase('selected');
    }
  };

  const pollJob = (jobId) => {
    procRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/jobs/${jobId}`, { headers: authHeaders() });
        const status = await res.json();
        if (!res.ok) throw new Error(status.detail || 'Lost track of the job');
        if (status.status === 'processing' || status.status === 'queued') {
          setStage(status.stage || 0);
        } else if (status.status === 'done') {
          clearInterval(procRef.current);
          const rRes = await fetch(`/api/jobs/${jobId}/results`, { headers: authHeaders() });
          const data = await rRes.json();
          if (!rRes.ok) throw new Error(data.detail || 'Could not load results');
          setResults(data);
          setPhase('done');
        } else if (status.status === 'failed') {
          clearInterval(procRef.current);
          setError(status.error || 'Processing failed');
          setPhase('selected');
        }
      } catch (err) {
        clearInterval(procRef.current);
        setError(err.message || 'Connection lost');
        setPhase('selected');
      }
    }, 2000);
  };

  const reset = () => {
    clearInterval(upRef.current); clearInterval(procRef.current);
    setPhase('idle'); setFile(null); setProgress(0); setStage(0); setResults(null); setError(null);
    setShpFile(null);
    if (inputRef.current) inputRef.current.value = '';
    if (shpRef.current) shpRef.current.value = '';
  };

  const downloadXlsx = () => {
    if (!R || !R.d1) return;
    const data = R.d1.map(d => {
      const l = d.left != null ? d.left : null;
      const r = d.right != null ? d.right : null;
      const avg = (l != null && r != null) ? (l + r) / 2 : (l || r);
      return {
        'Chainage (km)': (d.m / 1000).toFixed(3),
        'MPD Macrotexture Left': l?.toFixed(2) || '',
        'MPD Macrotexture Right': r?.toFixed(2) || '',
        'MPD Macrotexture Average': avg?.toFixed(2) || '',
        'Latitude (deg)': d.lat != null ? d.lat : '',
        'Longitude (deg)': d.lon != null ? d.lon : ''
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MPD Data");
    XLSX.writeFile(wb, (file?.name || 'output').replace(/\.(las|laz)$/i, '') + '_mpd1m.xlsx');
  };

  const R = results ? {
    d1: results.d1 || [],
    d10: results.d10 || [],
    meanLeft: results.mean_mpd_left ?? results.meanLeft ?? 0,
    meanRight: results.mean_mpd_right ?? results.meanRight ?? 0,
    lengthKm: results.length_km ?? results.lengthKm ?? 0,
    georeferenced: results.georeferenced ?? false,
  } : null;

  return (
    <div className="rv-root" style={{ background: C.canvas, minHeight: 560, fontFamily: 'system-ui, -apple-system, sans-serif', color: C.ink, padding: '24px 20px' }}>
      <style>{`
        .rv-root *{box-sizing:border-box}
        .rv-mono{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
        .rv-btn{font:inherit;font-size:14px;border-radius:10px;padding:11px 18px;cursor:pointer;border:none;transition:background .15s;display:inline-flex;align-items:center;gap:8px}
        .rv-primary{background:${C.petrol};color:#fff}.rv-primary:hover{background:${C.petrolDark}}
        .rv-ghost{background:transparent;color:${C.sub};border:1px solid ${C.line}}.rv-ghost:hover{color:${C.ink};border-color:${C.ink}}
        @keyframes laneMove{from{background-position-y:0}to{background-position-y:48px}}
        .rv-lane{animation:laneMove 1.1s linear infinite}
        @keyframes spin{to{transform:rotate(360deg)}}.rv-spin{animation:spin .9s linear infinite}
        @media(prefers-reduced-motion:reduce){.rv-lane,.rv-spin{animation:none}}
        .rv-grid2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
        @media(max-width:740px){.rv-grid2{grid-template-columns:1fr}}
      `}</style>

      {logModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: C.card, width: '100%', maxWidth: 800, maxHeight: '80vh', borderRadius: 14, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.line}`, display: 'flex', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Job Logs</h3>
              <button className="rv-ghost rv-btn" style={{ padding: 4 }} onClick={() => setLogModal({ open: false, content: '', loading: false })}><X size={16} /></button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1, background: C.ink, color: '#fff', borderBottomLeftRadius: 14, borderBottomRightRadius: 14 }}>
              {logModal.loading ? (
                 <Loader2 className="rv-spin" size={24} />
              ) : (
                 <pre style={{ margin: 0, fontSize: 12, fontFamily: 'ui-monospace, monospace', whiteSpace: 'pre-wrap' }}>{logModal.content}</pre>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <RoadVisionLogo height={32} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {token && (
              <>
                <button className="rv-ghost rv-btn" style={{ padding: '6px 10px', fontSize: 12 }}
                  onClick={() => { fetchHistory(); setShowHistory(!showHistory); }}>
                  <Clock size={13} /> History
                </button>
                <button className="rv-ghost rv-btn" style={{ padding: '6px 10px', fontSize: 12 }} onClick={logout}>
                  <LogOut size={13} /> {username}
                </button>
              </>
            )}
          </div>
        </header>
        <div style={{ height: 1, marginTop: 14, borderTop: `1.5px dashed ${C.line}` }} />

        {!token ? (
          <div style={{ maxWidth: 380, margin: '80px auto 0' }}>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, marginBottom: 8 }}>
                <RoadVisionLogo height={48} />
              </div>
              <div className="rv-mono" style={{ fontSize: 11.5, color: C.sub }}>Sign in to continue</div>
            </div>
            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: '28px 24px' }}>
              <div style={{ marginBottom: 16 }}>
                <label className="rv-mono" style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: C.sub, display: 'block', marginBottom: 6 }}>Username</label>
                <input type="text" value={loginUser} onChange={(e) => setLoginUser(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: C.canvas }} />
              </div>
              <div style={{ marginBottom: 22 }}>
                <label className="rv-mono" style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: C.sub, display: 'block', marginBottom: 6 }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPass ? 'text' : 'password'} value={loginPass} onChange={(e) => setLoginPass(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                    style={{ width: '100%', padding: '10px 40px 10px 12px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: C.canvas }} />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: C.sub }}>
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {loginError && (
                <div style={{ marginBottom: 16, background: C.errorBg, border: `1px solid ${C.errorBorder}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.error }}>{loginError}</div>
              )}
              <button className="rv-btn rv-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={handleLogin} disabled={loginLoading}>
                {loginLoading ? <Loader2 className="rv-spin" size={16} /> : 'Sign in'}
              </button>
            </div>
          </div>
        ) : (
        <main style={{ marginTop: 22 }}>
          {showHistory && (
            <div style={{ marginBottom: 20, background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: '20px 22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div className="rv-mono" style={{ fontSize: 10.5, letterSpacing: 1.4, textTransform: 'uppercase', color: C.sub }}>Processing history</div>
                <button className="rv-ghost rv-btn" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => setShowHistory(false)}><X size={13} /></button>
              </div>
              {history.length === 0 ? (
                <div style={{ fontSize: 13, color: C.sub }}>No jobs found.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {history.map((j) => (
                    <div key={j.job_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: C.canvas, flexWrap: 'wrap' }}>
                      <div style={{ width: 8, height: 8, borderRadius: 99, flexShrink: 0,
                        background: j.status === 'done' ? C.petrol : j.status === 'failed' ? '#B91C1C' : '#D97706' }} />
                      <div style={{ flex: 1, minWidth: 120 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.filename}</div>
                        <div className="rv-mono" style={{ fontSize: 10.5, color: C.sub, marginTop: 2 }}>
                          {new Date(j.created_at).toLocaleString('pt-PT', {dateStyle: 'short', timeStyle: 'short'}).replace(',', '')} · {j.status}{j.error ? `, ${j.error}` : ''}{userRole === 'admin' && j.submitted_by ? ` · ${j.submitted_by}` : ''}
                        </div>
                      </div>
                      {j.status === 'done' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          {userRole === 'admin' && (
                            <button className="rv-ghost rv-btn" style={{ padding: '6px 12px', fontSize: 11 }}
                              onClick={() => fetchLog(j.job_id)}>Logs</button>
                          )}
                          <button className="rv-btn rv-primary" style={{ padding: '6px 12px', fontSize: 11 }}
                            onClick={() => loadHistoryJob(j.job_id, j.filename)}>View results</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {(phase === 'idle' || phase === 'selected') && (
            <>
              {phase === 'idle' ? (
                <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
                  onClick={() => inputRef.current?.click()}
                  style={{ position: 'relative', overflow: 'hidden', borderRadius: 16, background: C.asphalt, cursor: 'pointer', border: `2px solid ${drag ? C.petrol : 'transparent'}` }}>
                  <div className="rv-lane" style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: 6, transform: 'translateX(-50%)', backgroundImage: `repeating-linear-gradient(${drag ? C.petrol : C.laneDash} 0 26px, transparent 26px 48px)`, opacity: drag ? .85 : .4 }} />
                  <div style={{ position: 'relative', textAlign: 'center', padding: '52px 24px' }}>
                    <div style={{ width: 50, height: 50, margin: '0 auto', borderRadius: 12, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Upload size={22} color="#fff" /></div>
                    <div style={{ marginTop: 16, fontSize: 19, color: '#fff', fontWeight: 500 }}>Drop a point cloud to measure</div>
                    <div className="rv-mono" style={{ marginTop: 8, fontSize: 12.5, color: 'rgba(255,255,255,0.6)' }}>LAS · up to about 2 GB · both lanes analysed</div>
                    <input ref={inputRef} type="file" accept=".las,.laz" onChange={onInput} style={{ display: 'none' }} />
                    <button className="rv-btn rv-primary" style={{ marginTop: 20 }} onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}>Browse files</button>
                  </div>
                </div>
              ) : (
                <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: C.petrolTint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FileIcon size={18} color={C.petrolDark} /></div>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
                    <div className="rv-mono" style={{ fontSize: 12, color: C.sub, marginTop: 1 }}>{file.size}</div>
                  </div>
                  <button className="rv-ghost rv-btn" style={{ padding: '9px 12px' }} onClick={reset}><X size={15} /> Remove</button>
                  <input ref={inputRef} type="file" accept=".las,.laz" onChange={onInput} style={{ display: 'none' }} />
                </div>
              )}

              {error && (
                <div style={{ marginTop: 12, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <X size={16} color="#B91C1C" style={{ flexShrink: 0 }} /><span style={{ fontSize: 13.5, color: '#991B1B' }}>{error}</span>
                </div>
              )}

              <div style={{ marginTop: 12, background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: '14px 16px' }}>
                <div className="rv-mono" style={{ fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.sub, marginBottom: 8 }}>Reference shapefile <span style={{ color: '#B91C1C' }}>*</span></div>
                {shpFile ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FileIcon size={15} color={C.petrolDark} />
                    <span style={{ fontSize: 13, flex: 1 }}>{shpFile.names.join(', ')}</span>
                    <button className="rv-ghost rv-btn" style={{ padding: '5px 8px', fontSize: 11 }}
                      onClick={() => { setShpFile(null); if (shpRef.current) shpRef.current.value = ''; }}><X size={13} /> Remove</button>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 12.5, color: C.sub, marginBottom: 8 }}>Select 3 files: .shp, .dbf and .prj</div>
                    <button className="rv-btn rv-ghost" style={{ fontSize: 12, padding: '7px 12px' }}
                      onClick={() => shpRef.current?.click()}>Choose files</button>
                    <input ref={shpRef} type="file" accept=".shp,.dbf,.prj" multiple onChange={onShpInput} style={{ display: 'none' }} />
                  </div>
                )}
              </div>

              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <button className="rv-btn rv-primary" onClick={start} disabled={!(phase === 'selected' && shpFile)}
                  style={{ opacity: (phase === 'selected' && shpFile) ? 1 : 0.5, cursor: (phase === 'selected' && shpFile) ? 'pointer' : 'not-allowed' }}>Start processing</button>
              </div>
            </>
          )}

          {phase === 'uploading' && (
            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: '30px 28px' }}>
              <div className="rv-mono" style={{ fontSize: 10.5, letterSpacing: 1.5, textTransform: 'uppercase', color: C.sub }}>Uploading</div>
              <div style={{ marginTop: 8, fontSize: 16, fontWeight: 500 }}>{file?.name}</div>
              <div style={{ marginTop: 20, height: 8, background: C.canvas, borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progress}%`, background: C.petrol, borderRadius: 99, transition: 'width .15s linear' }} />
              </div>
              <div style={{ marginTop: 9, display: 'flex', justifyContent: 'space-between' }}>
                <span className="rv-mono" style={{ fontSize: 12, color: C.sub }}>Uploading your point cloud. Please keep this tab open.</span>
                <span className="rv-mono" style={{ fontSize: 13, color: C.ink }}>{progress}%</span>
              </div>
            </div>
          )}

          {phase === 'processing' && (
            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: '26px 28px' }}>
              <div className="rv-mono" style={{ fontSize: 10.5, letterSpacing: 1.5, textTransform: 'uppercase', color: C.sub }}>
                Processing · step {String(stage + 1).padStart(2, '0')} / {String(STAGES.length).padStart(2, '0')}
              </div>
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {STAGES.map((s, i) => {
                  const done = i < stage, active = i === stage;
                  return (
                    <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '10px 0' }}>
                      <div style={{ width: 26, height: 26, borderRadius: 99, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: done ? C.petrol : active ? C.petrolTint : C.canvas, border: `1px solid ${done ? C.petrol : active ? C.petrol : C.line}` }}>
                        {done ? <Check size={14} color="#fff" /> : active ? <Loader2 className="rv-spin" size={14} color={C.petrolDark} /> : <span className="rv-mono" style={{ fontSize: 11, color: C.sub }}>{i + 1}</span>}
                      </div>
                      <span style={{ fontSize: 15, color: done || active ? C.ink : C.sub, fontWeight: active ? 500 : 400 }}>{s}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px dashed ${C.line}` }}>
                <span className="rv-mono" style={{ fontSize: 12, color: C.sub }}>Estimated processing time: about 20 minutes.</span>
              </div>
            </div>
          )}

          {phase === 'done' && R && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div className="rv-mono" style={{ fontSize: 10.5, letterSpacing: 1.5, textTransform: 'uppercase', color: C.sub }}>Results</div>
                  <div style={{ marginTop: 5, fontSize: 17, fontWeight: 500 }}>{file?.name}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="rv-ghost rv-btn" onClick={reset}>Measure another file</button>
                  <button className="rv-primary rv-btn" onClick={downloadXlsx}><Download size={16} /> Download XLSX</button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
                <Readout label="Mean MPD Left" value={R.meanLeft.toFixed(2)} unit="mm" />
                <Readout label="Mean MPD Right" value={R.meanRight.toFixed(2)} unit="mm" />
                <Readout label="Section length" value={R.lengthKm} unit="km" />
              </div>
              {R.georeferenced && R.d1[0]?.lat != null && <SectionMap data={R.d1} />}
              <div style={{ marginTop: 20 }}>
                <div className="rv-mono" style={{ fontSize: 10.5, letterSpacing: 1.4, textTransform: 'uppercase', color: C.sub, marginBottom: 10 }}>1 m resolution. Drag the bar below to navigate</div>
                <div className="rv-grid2">
                  <MpdChart title="Left lane" subtitle="MPD every 1 m · mm" data={R.d1.filter(d => d.left != null)} dataKey="left" mean={R.meanLeft} color={C.petrol} showBrush={true} brushLen={999} />
                  <MpdChart title="Right lane" subtitle="MPD every 1 m · mm" data={R.d1.filter(d => d.right != null)} dataKey="right" mean={R.meanRight} color={C.petrol} showBrush={true} brushLen={999} />
                </div>
              </div>
            </>
          )}
        </main>
        )}
      </div>
    </div>
  );
}